/**
 * SelectedProductPreview.tsx
 *
 * Quote-bubble preview of a product the user picked from the marketplace
 * product picker, shown above the chat input row. Extracted from
 * ChatInputArea.tsx to keep that file under the per-file LOC budget.
 */

import { FC } from "react";
import type { Product } from "src/app/modules/product/types/ProductListing";

const formatProductPrice = (price: number, currency?: string): string => {
  if (currency === "IDR") {
    if (price >= 1_000_000) return `Rp ${(price / 1_000_000).toFixed(1)}M`;
    if (price >= 1_000) return `Rp ${Math.round(price / 1_000)}k`;
    return `Rp ${price}`;
  }
  return `${currency ?? ""} ${price}`.trim();
};

export interface SelectedProductPreviewProps {
  product: Product;
  onClear: () => void;
}

const SelectedProductPreview: FC<SelectedProductPreviewProps> = ({
  product,
  onClear,
}) => {
  return (
    <div
      id="chat-selected-product-preview"
      data-testid="selected-product-preview"
      className="chat-composer-preview-row fade-in"
    >
      <div className="chat-composer-preview-card">
        {product.mainImage ? (
          <img
            src={product.mainImage}
            alt={product.title}
            className="chat-composer-preview-thumb"
          />
        ) : (
          <div className="chat-composer-preview-thumb chat-composer-preview-thumb-empty">
            No img
          </div>
        )}
        <div className="chat-composer-preview-content">
          <div className="chat-composer-preview-eyebrow">
            Product selected
          </div>
          <div
            className="chat-composer-preview-title chat-composer-preview-title-multiline"
            title={product.title}
          >
            {product.title}
          </div>
          <div className="chat-composer-preview-meta">
            {[
              product.sku,
              formatProductPrice(
                product.price.current,
                product.price.currency
              ),
              product.status === "sold_out" || product.totalStock === 0
                ? "Out of stock"
                : `Stock ${product.totalStock}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      </div>
      <button
        type="button"
        data-testid="selected-product-clear"
        className="chat-composer-preview-clear"
        onClick={onClear}
        aria-label="Clear selected product"
      >
        <i className="bi bi-x-lg" />
      </button>
    </div>
  );
};

export default SelectedProductPreview;
